/**
 * "What Happens Next": the rings down the side, the products across the top, and in each cell, in
 * words, what that ring's machines get next and when. The ring's head carries the one channel
 * control and a menu for pushing an install or a rollback now; approving happens in the cell.
 *
 * The rules mirror `chooseRelease` in packages/api/src/releases/service.ts: a ring takes its own
 * channel, else the product's (the "Default" here); in Automatically mode the newest release on
 * that channel is offered once `deferDays` have passed since it was published; in When I approve
 * mode only the approved version is offered.
 *
 * Laid out as a list group (Layout.tsx): the default-channel choice as a row, then the grid flush in
 * the card. The grid answers to the group's width (a column of rings below 48rem). Adding a ring and
 * ordering an install or a rollback open a FormSheet.
 */

import { compareVersions, type ReleaseChannel, type ReleaseManifest, type ReleaseProduct, type ReleaseProductView, type ReleaseRing, type ReleasesOverview, type UpdateOrderProduct } from '@aioe/shared';
import { dateMedium } from '@aioe/shared/wording/dateTime';
import { Button, Input, TextField } from '@heroui/react';
import { useState, type CSSProperties, type ReactNode } from 'react';

import { problemText, routes, type DeviceRow } from '../api/client.ts';
import { ConfirmDialog, ErrorNote, ListBlock, ListGroup, ListRow } from './ui.tsx';
import { FormField, FormSheet } from './ui/FormSheet.tsx';
import { RowMenu } from './ui/RowMenu.tsx';
import { OrderControl } from './updates.tsx';
import { OptionSelect } from './updatesFields.tsx';
import { patchProducts, patchRings, PRODUCT_NAME } from './updatesPolicy.ts';

type Act = (f: () => Promise<unknown>) => Promise<void>;
type MarkState = 'online' | 'working' | 'idle' | 'medium';

const DAY = 86_400_000;
const shortDay = (ms: number) => dateMedium(ms, { year: false });
const channelWord = (c: ReleaseChannel) => (c === 'stable' ? 'Stable' : 'Nightly');

/** The products a ring's machines run (the platform itself is not updated by rings). */
export function fleetProducts(view: ReleasesOverview): ReleaseProductView[] {
  return view.products.filter((p) => p.product !== 'aioe');
}

/** The newest release on a channel that is not withdrawn. */
function newestOn(p: ReleaseProductView, channel: ReleaseChannel): ReleaseManifest | undefined {
  let best: ReleaseManifest | undefined;
  for (const r of p.releases) if (r.channel === channel && !r.yanked && (!best || compareVersions(r.version, best.version) > 0)) best = r;
  return best;
}

/** The release a ring's machines would take next and when, before anyone acts. */
export interface NextStep {
  /** The version offered now, and how many machines in the ring still run something older. */
  offered?: string;
  behind?: number;
  /** A newer release the ring is not offered yet: after a wait (`on`) or after an approval. */
  pending?: { version: string; on?: number; approve?: boolean };
}

export function nextStep(p: ReleaseProductView, ring: ReleaseRing, running?: Map<string, number>): NextStep {
  const channel = ring.channel ?? p.policy.channel;
  const newest = newestOn(p, channel);
  const offered = p.offered[ring.id];
  const step: NextStep = {};
  if (offered) {
    step.offered = offered;
    if (running) step.behind = [...running].reduce((n, [v, c]) => n + (compareVersions(v, offered) < 0 ? c : 0), 0);
    else step.behind = (p.ringSizes[ring.id] ?? 0) > 0 && p.running.some((r) => compareVersions(r.version, offered) < 0) ? p.ringSizes[ring.id] : 0;
  }
  if (newest && (!offered || compareVersions(newest.version, offered) > 0)) {
    if (p.policy.mode === 'approve') step.pending = { version: newest.version, approve: true };
    else if (p.policy.deferDays > 0) step.pending = { version: newest.version, on: Date.parse(newest.publishedAt) + p.policy.deferDays * DAY };
  }
  return step;
}

/** Releases waiting on a person: distinct product and version pairs some ring with machines needs approved. */
export function waitingApprovals(view: ReleasesOverview): number {
  const waiting = new Set<string>();
  for (const p of fleetProducts(view)) {
    if (p.policy.mode !== 'approve') continue;
    for (const ring of view.rings) {
      if ((p.ringSizes[ring.id] ?? 0) === 0) continue;
      const s = nextStep(p, ring);
      if (s.pending?.approve) waiting.add(`${p.product}@${s.pending.version}`);
    }
  }
  return waiting.size;
}

function kindOf(product: ReleaseProduct): 'desktop' | 'node' | undefined {
  return product === 'workbench' ? 'desktop' : product === 'node' ? 'node' : undefined;
}

/** Ring id -> product -> version -> machines, from the fleet list. */
function runningByRing(view: ReleasesOverview, devices: DeviceRow[] | undefined): Map<string, Map<ReleaseProduct, Map<string, number>>> | undefined {
  if (!devices) return undefined;
  const out = new Map<string, Map<ReleaseProduct, Map<string, number>>>();
  for (const d of devices) {
    const ring = view.ringOf[d.deviceId];
    if (!ring) continue;
    const byProduct = out.get(ring) ?? new Map<ReleaseProduct, Map<string, number>>();
    for (const product of ['workbench', 'node', 'mesh'] as const) {
      const kind = kindOf(product);
      const version = product === 'mesh' ? d.components?.mesh : kind === d.kind ? d.appVersion : undefined;
      if (!version) continue;
      const counts = byProduct.get(product) ?? new Map<string, number>();
      counts.set(version, (counts.get(version) ?? 0) + 1);
      byProduct.set(product, counts);
    }
    out.set(ring, byProduct);
  }
  return out;
}

/** How a product's releases reach the rings, said in the column head. */
function modeLine(p: ReleaseProductView): string {
  if (p.policy.mode === 'approve') return 'When you approve';
  return p.policy.deferDays ? `Automatically, ${p.policy.deferDays} ${p.policy.deferDays === 1 ? 'day' : 'days'} after release` : 'Automatically';
}

/** A dot and words that wrap: a cell is narrow and nightly versions are long. */
function Mark({ state, children }: { state: MarkState; children: ReactNode }) {
  return (
    <span className="flex items-baseline gap-1.5 text-sm">
      <span className="status-dot shrink-0 translate-y-[-1px]" data-state={state} aria-hidden="true" />
      <span className="min-w-0 break-words">{children}</span>
    </span>
  );
}

function Cell({ product: p, ring, running, busy, act }: { product: ReleaseProductView; ring: ReleaseRing; running?: Map<string, number>; busy: boolean; act: Act }) {
  const step = nextStep(p, ring, running);
  const machines = running ? [...running.values()].reduce((a, b) => a + b, 0) : (p.ringSizes[ring.id] ?? 0);
  const versions = running ? [...running].sort((a, b) => b[1] - a[1]) : [];
  const top = versions[0];
  const v = (s: string) => <span className="mono">{s}</span>;
  return (
    <div className="grid min-w-0 content-start gap-1">
      <span className="text-xs @3xl:hidden">
        <span className="font-medium">{PRODUCT_NAME[p.product]}</span> <span className="text-muted">· {modeLine(p)}</span>
      </span>
      <span className="text-xs text-muted">
        {machines === 0 ? (
          'No machines yet'
        ) : top ? (
          <>
            Running {v(top[0])} <span className="tabular-nums">({top[1]})</span>
            {versions.length > 1 ? `, ${versions.length - 1} other ${versions.length === 2 ? 'version' : 'versions'}` : ''}
          </>
        ) : (
          <span className="tabular-nums">
            {machines} {machines === 1 ? 'machine' : 'machines'}
          </span>
        )}
      </span>
      {step.offered && (step.behind ?? 0) > 0 ? <Mark state="working">→ gets {v(step.offered)} now</Mark> : null}
      {step.pending?.on !== undefined ? <Mark state="idle">→ {v(step.pending.version)} on {shortDay(step.pending.on)}</Mark> : null}
      {step.pending?.approve ? (
        <>
          <Mark state="medium">→ {v(step.pending.version)} when you approve</Mark>
          <Button size="sm" variant="secondary" className="w-fit" isDisabled={busy} onPress={() => void act(() => routes.approveRelease(p.product, step.pending!.version, ring.id))}>
            Approve
          </Button>
        </>
      ) : null}
      {!step.pending && !((step.behind ?? 0) > 0) ? machines === 0 ? <Mark state="idle">{step.offered ? <>Offered {v(step.offered)}</> : 'Nothing offered yet'}</Mark> : step.offered || p.policy.mode === 'follow' ? <Mark state="online">Up to date</Mark> : <Mark state="idle">Nothing approved yet</Mark> : null}
    </div>
  );
}

function matchText(r: ReleaseRing): string {
  if (!r.match) return 'Everyone not in another ring';
  const parts = [r.match.kind === 'desktop' ? 'Desktops' : r.match.kind === 'node' ? 'Nodes' : undefined, r.match.tags?.length ? `tagged ${r.match.tags.join(', ')}` : undefined, r.match.deviceIds?.length ? `${r.match.deviceIds.length} named ${r.match.deviceIds.length === 1 ? 'machine' : 'machines'}` : undefined].filter(Boolean);
  return parts.length ? parts.join(', ') : 'Everyone';
}

type RingChannel = 'default' | ReleaseChannel;

function RingRow({ ring, view, products, running, defaultWord, busy, act, cols }: { ring: ReleaseRing; view: ReleasesOverview; products: ReleaseProductView[]; running?: Map<ReleaseProduct, Map<string, number>>; defaultWord: string; busy: boolean; act: Act; cols: CSSProperties }) {
  const [ordering, setOrdering] = useState(false);
  const [done, setDone] = useState<string>();
  const [removing, setRemoving] = useState(false);
  const size = view.ringSizes[ring.id] ?? 0;
  const removable = view.rings.length > 1 && !!ring.match;
  const orderable = (['workbench', 'node', 'mesh'] as UpdateOrderProduct[]).filter((x) => products.some((p) => p.product === x));
  const withMachines = orderable.filter((x) => (products.find((p) => p.product === x)?.ringSizes[ring.id] ?? 0) > 0);
  const setChannel = (next: RingChannel) =>
    act(() =>
      patchRings((rings) =>
        rings.map((r) => {
          if (r.id !== ring.id) return r;
          const { channel: _c, ...rest } = r;
          return next === 'default' ? rest : { ...rest, channel: next };
        }),
      ),
    );
  const who = size ? `Its ${size === 1 ? 'machine joins' : `${size} machines join`} the next ring ${size === 1 ? 'it matches' : 'they match'} and ${size === 1 ? 'gets' : 'get'} what that ring is offered.` : 'No machine is in it now.';
  const remove = () => {
    setRemoving(false);
    void act(async () => {
      const p = await routes.releasePolicy();
      const productsNext = Object.fromEntries(Object.entries(p.products).map(([k, pp]) => [k, pp ? { ...pp, approved: Object.fromEntries(Object.entries(pp.approved).filter(([r]) => r !== ring.id)) } : pp]));
      await routes.saveReleasePolicy({ rings: p.rings.filter((r) => r.id !== ring.id), products: productsNext });
    });
  };
  return (
    <div className="px-4 py-3">
      <div className="grid gap-3 @3xl:grid-cols-[minmax(11rem,14rem)_repeat(var(--cols),minmax(0,1fr))] @3xl:gap-x-5" style={cols}>
        <div className="grid min-w-0 content-start gap-2">
          <div className="flex min-w-0 items-start gap-1">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">{ring.name}</div>
              <div className="text-xs text-muted">
                {matchText(ring)} · <span className="tabular-nums">{size}</span> {size === 1 ? 'machine' : 'machines'}
              </div>
            </div>
            <RowMenu
              label={`More for ${ring.name}`}
              items={[
                { id: 'order', label: 'Install now or roll back…', disabled: busy },
                removable && { id: 'remove', label: 'Remove ring', danger: true, disabled: busy },
              ]}
              onAction={(key) => (key === 'order' ? (setOrdering(true), setDone(undefined)) : key === 'remove' ? setRemoving(true) : undefined)}
            />
          </div>
          <OptionSelect<RingChannel>
            ariaLabel={`Builds ${ring.name} takes`}
            className="w-full max-w-60"
            value={ring.channel ?? 'default'}
            isDisabled={busy}
            options={[
              { id: 'default', label: `Default: ${defaultWord}` },
              { id: 'stable', label: 'Stable builds' },
              { id: 'nightly', label: 'Nightly builds' },
            ]}
            onChange={(k) => void setChannel(k)}
          />
        </div>
        {products.map((p) => (
          <Cell key={p.product} product={p} ring={ring} running={running?.get(p.product) ?? (running ? new Map() : undefined)} busy={busy} act={act} />
        ))}
      </div>
      {done ? <p className="mt-2 text-xs text-muted">{done}</p> : null}
      <ConfirmDialog isOpen={removing} title={`Remove the ring ${ring.name}`} confirmLabel="Remove" danger onCancel={() => setRemoving(false)} onConfirm={remove}>
        {who} What is already installed stays installed, and the ring's approvals go with it.
      </ConfirmDialog>
      <FormSheet isOpen={ordering} width="md" title={`Install now or roll back ${ring.name}`} description="Every machine in the ring is told on its next heartbeat, within a minute." onClose={() => setOrdering(false)}>
        {ordering ? (
          <OrderControl
            compact
            products={withMachines.length ? withMachines : orderable}
            overview={view}
            onSend={async (body) => {
              const { product, ...rest } = body;
              const created = await routes.orderRingUpdate(product, ring.id, rest);
              setDone(`${created.length} ${created.length === 1 ? 'machine' : 'machines'} in ${ring.name} told on their next heartbeat, within a minute.`);
              setOrdering(false);
            }}
          />
        ) : null}
      </FormSheet>
    </div>
  );
}

function AddRing({ isOpen, busy, act, onDone }: { isOpen: boolean; busy: boolean; act: Act; onDone: () => void }) {
  const [name, setName] = useState('');
  const [tags, setTags] = useState('');
  const [kind, setKind] = useState<'any' | 'desktop' | 'node'>('any');
  const [channel, setChannel] = useState<RingChannel>('default');
  const [error, setError] = useState<string>();
  const close = () => {
    setName('');
    setTags('');
    setKind('any');
    setChannel('default');
    setError(undefined);
    onDone();
  };
  // The sheet says what went wrong itself: the page's own error line is behind it.
  const add = () =>
    act(async () => {
      setError(undefined);
      try {
        await addRing();
        close();
      } catch (e) {
        setError(problemText(e));
      }
    });
  const addRing = async () => {
    const id = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    if (!id) throw new Error('Give the ring a name.');
    const match: ReleaseRing['match'] = {
      ...(tags.trim() ? { tags: tags.split(',').map((t) => t.trim()).filter(Boolean) } : {}),
      ...(kind !== 'any' ? { kind } : {}),
    };
    const ring: ReleaseRing = { id, name: name.trim(), ...(Object.keys(match).length ? { match } : {}), ...(channel !== 'default' ? { channel } : {}) };
    await patchRings((rings) => {
      if (rings.some((r) => r.id === id)) throw new Error('A ring with that name exists.');
      // New rings go before the catch-all, which stays last.
      const last = rings[rings.length - 1]!;
      return last.match ? [...rings, ring] : [...rings.slice(0, -1), ring, last];
    });
  };
  return (
    <FormSheet
      isOpen={isOpen}
      width="md"
      title="Add a ring"
      description="A machine joins the first ring it matches. A new ring goes before the last one, which takes everyone else."
      onClose={close}
      footer={
        <>
          <Button size="sm" variant="tertiary" onPress={close}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" isDisabled={busy || !name.trim()} onPress={() => void add()}>
            Add ring
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) void add();
        }}
      >
        <FormField label="Name">
          <TextField aria-label="Ring name" value={name} onChange={setName} autoFocus>
            <Input placeholder="For example, Canary" />
          </TextField>
        </FormField>
        <FormField label="Tags" hint="Machines with any of these tags join the ring. Separate them with commas; leave it empty for every machine of the kind below.">
          <TextField aria-label="Tags" value={tags} onChange={setTags}>
            <Input placeholder="Optional" />
          </TextField>
        </FormField>
        <FormField label="Kind of machine">
          <OptionSelect
            ariaLabel="Kind of machine"
            value={kind}
            options={[
              { id: 'any', label: 'Desktops and nodes' },
              { id: 'desktop', label: 'Desktops only' },
              { id: 'node', label: 'Nodes only' },
            ]}
            onChange={setKind}
          />
        </FormField>
        <FormField label="Builds" hint="Default follows the choice for rings left on Default.">
          <OptionSelect<RingChannel>
            ariaLabel="Builds this ring takes"
            value={channel}
            options={[
              { id: 'default', label: 'Default builds' },
              { id: 'stable', label: 'Stable builds' },
              { id: 'nightly', label: 'Nightly builds' },
            ]}
            onChange={setChannel}
          />
        </FormField>
        <ErrorNote error={error} />
      </form>
    </FormSheet>
  );
}

export function WhatHappensNext({ view, devices, busy, act }: { view: ReleasesOverview; devices: DeviceRow[] | undefined; busy: boolean; act: Act }) {
  const [adding, setAdding] = useState(false);
  const products = fleetProducts(view);
  const running = runningByRing(view, devices);
  const channels = new Set(products.map((p) => p.policy.channel));
  const common: ReleaseChannel | null = channels.size === 1 ? [...channels][0]! : null;
  const defaultWord = common ? channelWord(common) : 'each product’s own';
  const cols = { '--cols': products.length } as CSSProperties;
  return (
    <>
    <ListGroup
      title="What happens next"
      count={view.rings.length}
      description="A machine joins the first ring it matches; the last ring takes everyone else."
      actions={
        <Button size="sm" variant="secondary" isDisabled={busy} onPress={() => setAdding(true)}>
          Add ring
        </Button>
      }
    >
      <ListRow
        title="Builds for rings left on Default"
        description={common ? 'A ring takes these unless it names its own.' : 'The products differ; choosing sets them all.'}
        control={
          <OptionSelect<ReleaseChannel>
            ariaLabel="Builds rings on Default take"
            className="w-40"
            value={common}
            placeholder="Mixed"
            isDisabled={busy}
            options={[
              { id: 'stable', label: 'Stable builds' },
              { id: 'nightly', label: 'Nightly builds' },
            ]}
            onChange={(channel) => void act(() => patchProducts(view, products.map((p) => p.product), { channel }))}
          />
        }
      />
      <ListBlock flush>
        <div className="divide-y divide-separator">
          <div className="hidden gap-x-5 px-4 py-2 @3xl:grid @3xl:grid-cols-[minmax(11rem,14rem)_repeat(var(--cols),minmax(0,1fr))]" style={cols}>
            <span className="text-xs font-medium text-muted">Ring</span>
            {products.map((p) => (
              <span key={p.product} className="min-w-0">
                <span className="block text-xs font-medium">{PRODUCT_NAME[p.product]}</span>
                <span className="block text-xs text-muted">{modeLine(p)}</span>
              </span>
            ))}
          </div>
          {view.rings.map((ring) => (
            <RingRow key={ring.id} ring={ring} view={view} products={products} running={running ? (running.get(ring.id) ?? new Map()) : undefined} defaultWord={defaultWord} busy={busy} act={act} cols={cols} />
          ))}
        </div>
      </ListBlock>
    </ListGroup>
    <AddRing isOpen={adding} busy={busy} act={act} onDone={() => setAdding(false)} />
    </>
  );
}
